import { Button, Progress } from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconBuildingCommunity,
  IconCheck,
  IconListCheck,
  IconUsers,
} from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { followLink, useShell } from '../../shell-context.js';
import { LoadError } from '../purchasing/table.js';
import classes from './Setup.module.css';
import {
  SETUP_PATH,
  SETUP_STEP_PATHS,
  setupProgress,
  stepComplete,
  stepDetail,
} from './setup-model.js';
import { type SetupSource, setupErrorCode } from './setup-source.js';
import type { SetupStatus, SetupStepItem, SetupWarningItem } from './setup-types.js';

const ICON = { size: 18, stroke: 1.8 } as const;

const TONE: Record<SetupStepItem['status'], StatusTone> = {
  done: 'success',
  todo: 'warning',
  default: 'info',
  customized: 'success',
};

/** Kabuk içi bağlantı: yeni sekme ve orta tık tarayıcıya kalır. */
function useLink() {
  const shell = useShell();
  return (path: string) => ({
    href: path,
    onClick: (event: MouseEvent<HTMLAnchorElement>) => {
      if (shell) followLink(event, path, shell.navigate);
    },
  });
}

/**
 * Settings sayfasındaki "Company Setup" kartı (ADR 0042 §22) [sorular #247]: yalnız kurulum
 * durumu okunabildiğinde (`settings.edit`) çizilir; ilerleme ve sihirbaza bağlantı.
 */
export function CompanySetupCard({ status }: { status: SetupStatus | null }) {
  const { t } = useTranslation();
  const link = useLink();
  if (!status) return null;
  const progress = setupProgress(status);
  return (
    <Panel title={t('setup.card.title')} icon={<IconBuildingCommunity {...ICON} />} divider>
      <div className={classes.cardBody}>
        <div className={classes.progress}>
          <p className={classes.stepText}>{t('setup.card.text')}</p>
          <p className={classes.progressValue}>
            {t('setup.card.progress', { done: progress.done, total: progress.total })}
          </p>
        </div>
        <Button component="a" variant="light" {...link(SETUP_PATH)}>
          {t('setup.card.open')}
        </Button>
      </div>
    </Panel>
  );
}

/**
 * Settings ▸ Users kartı (ortak 2026-10-30): şirket düzeyinde `users.view` olan herkes (ör. Crew
 * Manager) Kullanıcılar ekranına buradan gider; kurulum sihirbazı, rol ve matris yönetimi
 * `settings.edit`'te kalır.
 */
export function UsersCard({ visible }: { visible: boolean }) {
  const { t } = useTranslation();
  const link = useLink();
  if (!visible) return null;
  return (
    <Panel title={t('setup.users.card.title')} icon={<IconUsers {...ICON} />} divider>
      <div className={classes.cardBody}>
        <p className={classes.stepText}>{t('setup.users.card.text')}</p>
        <Button component="a" variant="light" {...link(SETUP_STEP_PATHS.users)}>
          {t('setup.users.card.open')}
        </Button>
      </div>
    </Panel>
  );
}

function statusLabel(t: TFunction, step: SetupStepItem) {
  return t(`setup.wizard.status.${step.status}`, {
    version: typeof step.detail.version === 'number' ? step.detail.version : 0,
  });
}

function warningText(t: TFunction, warning: SetupWarningItem) {
  return warning.code === 'NO_MASTER'
    ? t('setup.wizard.warning.NO_MASTER', { vessel: warning.vessel.name })
    : t(`setup.wizard.warning.${warning.code}`);
}

/**
 * Sihirbazın görünümü: sekiz adım sırasıyla, durumları ve ayrıntılarıyla; her adım var olan
 * ekranı açar. Sağda ilerleme, matris sürümü ve uyarılar (engellemez); altta sıradaki adım.
 */
export function SetupWizardView({ status }: { status: SetupStatus }) {
  const { t } = useTranslation();
  const link = useLink();
  const progress = setupProgress(status);
  return (
    <>
      <div className={classes.layout}>
        <Panel title={t('setup.wizard.steps')} icon={<IconListCheck {...ICON} />} divider flush>
          <ol className={classes.steps}>
            {status.steps.map((step, index) => {
              const detail = stepDetail(step);
              const title = t(`setup.wizard.step.${step.key}.title`);
              return (
                <li
                  key={step.key}
                  className={classes.step}
                  data-complete={stepComplete(step) || undefined}
                >
                  <span className={classes.stepMark} aria-hidden>
                    {stepComplete(step) ? <IconCheck size={16} stroke={2.4} /> : index + 1}
                  </span>
                  <div className={classes.stepBody}>
                    <h3 className={classes.stepTitle}>
                      {title}
                      <StatusBadge tone={TONE[step.status]}>{statusLabel(t, step)}</StatusBadge>
                    </h3>
                    <p className={classes.stepText}>{t(`setup.wizard.step.${step.key}.text`)}</p>
                    <p className={classes.stepDetail}>{t(detail.key, detail.values)}</p>
                  </div>
                  <Button
                    component="a"
                    variant={stepComplete(step) ? 'default' : 'light'}
                    size="xs"
                    aria-label={`${t('setup.wizard.open')}: ${title}`}
                    {...link(SETUP_STEP_PATHS[step.key])}
                  >
                    {t('setup.wizard.open')}
                  </Button>
                </li>
              );
            })}
          </ol>
        </Panel>
        <div className={classes.aside}>
          <Panel title={t('setup.wizard.progressTitle')} divider>
            <div className={classes.progress}>
              <p className={classes.progressValue}>
                {t('setup.wizard.progress', { done: progress.done, total: progress.total })}
              </p>
              <Progress
                value={(progress.done / Math.max(1, progress.total)) * 100}
                aria-label={t('setup.wizard.progressTitle')}
              />
              <p className={classes.stepText}>
                {t('setup.wizard.matrix')}:{' '}
                <strong>{t('setup.wizard.matrixValue', { version: status.matrixVersion })}</strong>
              </p>
            </div>
          </Panel>
          <Panel
            title={t('setup.wizard.warningsTitle')}
            icon={<IconAlertTriangle {...ICON} />}
            divider
          >
            {status.warnings.length === 0 ? (
              <p className={classes.stepText}>{t('setup.wizard.noWarnings')}</p>
            ) : (
              <ul className={classes.warnings}>
                {status.warnings.map((warning) => (
                  <li
                    key={
                      warning.code === 'NO_MASTER'
                        ? `${warning.code}-${warning.vessel.id}`
                        : warning.code
                    }
                  >
                    <IconAlertTriangle size={16} aria-hidden />
                    <span>{warningText(t, warning)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
      <div className={classes.footer}>
        {progress.next ? (
          <Button
            component="a"
            rightSection={<IconArrowRight size={16} />}
            {...link(SETUP_STEP_PATHS[progress.next.key])}
          >
            {t('setup.wizard.continue', {
              step: t(`setup.wizard.step.${progress.next.key}.title`),
            })}
          </Button>
        ) : (
          <StatusBadge tone="success" withIcon>
            {t('setup.wizard.complete')}
          </StatusBadge>
        )}
      </div>
    </>
  );
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; status: SetupStatus };

/** Şirket kurulumu sihirbazı (`/settings/setup`): durum veriden okunur, ekran bir şey yazmaz. */
export function SetupWizard({ source }: { source: SetupSource }) {
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', status: await source.setupStatus() });
    } catch (error) {
      setState({ kind: 'error', code: setupErrorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <div className={classes.screen}>
      <PageHeader
        title={t('setup.wizard.title')}
        description={t('setup.wizard.description')}
        crumb={t('setup.wizard.crumb')}
      />
      {state.kind === 'ready' ? (
        <SetupWizardView status={state.status} />
      ) : state.kind === 'error' ? (
        <Panel>
          <LoadError
            title={t('setup.wizard.loadError')}
            code={state.code}
            onRetry={() => void load()}
          />
        </Panel>
      ) : (
        <Panel>
          <EmptyState title={t('common.loading')} />
        </Panel>
      )}
    </div>
  );
}
